import { useEffect, useState } from 'react';
import { localStorage } from '@/infrastructure/storage';

const SOUND_KEY = 'notification_sound_enabled';
const MAX_PLAY_MS = 5000; // ← max 5 seconds, change as needed

// ── sound map — add new types here ───────────────────────────────────────────
const SOUND_MAP: Record<string, string> = {
    new_order:       '/sounds/new_order.mp3',
    order_cancelled: '/sounds/order_cancel.mp3',
    store_closed:    '/sounds/store_closed_long.mp3',
    default:         '/sounds/default.mp3',
};

// ── audio cache — one instance per sound file ─────────────────────────────────
const audioCache = new Map<string, HTMLAudioElement>();

let currentAudio:   HTMLAudioElement | null = null;
let stopTimer:      ReturnType<typeof setTimeout> | null = null;
let unlocked =      localStorage.get<string>(SOUND_KEY) === 'true';

function getAudio(src: string): HTMLAudioElement {
    if (!audioCache.has(src)) {
        const a = new Audio(src);
        a.volume = 1;
        audioCache.set(src, a);
    }
    return audioCache.get(src)!;
}

function stopCurrent() {
    if (stopTimer) {
        clearTimeout(stopTimer);
        stopTimer = null;
    }
    if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        currentAudio = null;
    }
}

// pre-load all sounds if previously unlocked
if (unlocked) {
    Object.values(SOUND_MAP).forEach(getAudio);
}

/**
 * Play sound based on notification type — fallback to default
 */
export function playNotificationSound(type?: string) {
    if (!unlocked) return;

    const src = (type && SOUND_MAP[type]) ? SOUND_MAP[type] : SOUND_MAP.default;
    const a   = getAudio(src);

    // stop current + clear its timer
    stopCurrent();

    currentAudio = a;
    a.currentTime = 0;
    a.play().catch((e) => {
        console.warn('[Sound] Play failed', e);
        unlocked = false;
    });

    // auto-stop after MAX_PLAY_MS
    stopTimer = setTimeout(stopCurrent, MAX_PLAY_MS);

    // also clear timer naturally if sound ends before limit
    a.onended = () => {
        if (stopTimer) {
            clearTimeout(stopTimer);
            stopTimer = null;
        }
        currentAudio = null;
    };
}

/**
 * Explicit unlock — MUST be called from direct user click
 */
export async function unlockNotificationSound(): Promise<void> {
    const a = getAudio(SOUND_MAP.default);

    try {
        await a.play();
        a.pause();
        a.currentTime = 0;
        unlocked = true;
        localStorage.set(SOUND_KEY, 'true');
        Object.values(SOUND_MAP).forEach(getAudio);
        console.log('🔓 Notification sound unlocked');
    } catch (e) {
        console.warn('❌ Unlock failed', e);
        throw e;
    }
}

/**
 * Hook to expose sound-enabled state
 */
export function useNotificationSoundState() {
    const [enabled, setEnabled] = useState(
        () => localStorage.get<string>(SOUND_KEY) === 'true'
    );

    useEffect(() => {
        if (enabled) {
            unlocked = true;
            Object.values(SOUND_MAP).forEach(getAudio);
        }
    }, [enabled]);

    return {
        enabled,
        enable: async () => {
            await unlockNotificationSound();
            setEnabled(true);
        },
        disable: () => {
            unlocked = false;
            stopCurrent();
            localStorage.remove(SOUND_KEY);
            setEnabled(false);
        },
    };
}